import { useCallback, useEffect, useRef } from 'react';

import { revealFirstInvalidField } from 'lib/helpers/revealFirstInvalidField';

// Validation can settle after the click (async Yup tests, server field errors set in onError),
// so keep watching the body for a while instead of checking once.
const WATCH_MS = 1500;

/**
 * Returns a function to call on a submit attempt: it scrolls to (and focuses) the first
 * invalid field in `bodyRef`, either one already showing an error or the first one to appear
 * within WATCH_MS.
 *
 * @param {React.RefObject<HTMLElement>} bodyRef
 */
const useRevealFirstError = (bodyRef) => {
  const stopRef = useRef(null);

  const stopWatching = useCallback(() => {
    stopRef.current?.();
    stopRef.current = null;
  }, []);

  const watchForErrors = useCallback(() => {
    stopWatching();
    const body = bodyRef.current;
    if (!body) return;

    let observer;
    let timeout;
    // One frame lets the click handler's own synchronous renders land first.
    const frame = requestAnimationFrame(() => {
      if (revealFirstInvalidField(body)) return;

      observer = new MutationObserver(() => {
        if (revealFirstInvalidField(body)) stopWatching();
      });
      observer.observe(body, {
        subtree: true,
        childList: true,
        attributeFilter: ['aria-invalid', 'data-field-error']
      });
      timeout = setTimeout(stopWatching, WATCH_MS);
    });

    stopRef.current = () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      clearTimeout(timeout);
    };
  }, [bodyRef, stopWatching]);

  useEffect(() => stopWatching, [stopWatching]);

  return watchForErrors;
};

export default useRevealFirstError;
